import { useRows, useCompleteRows, useAccess } from './api/data';
import { name, type GetPath, type Row } from './api/client';
import { fieldLabel, statusLabel } from './i18n';
import { Button, Empty, ErrorState, Loading } from './ui';

import { employeeStatusLabel } from './hr-permissions';
export { employeeStatusLabels } from './hr-permissions';
const profileFields = new Set([
  'workName',
  'workEmail',
  'workTelegram',
  'personalTelegram',
  'departmentId',
  'primaryTeamId',
  'additionalTeamIds',
  'positionId',
  'jobTitle',
  'seniorityId',
  'lineManagerId',
  'locationId',
  'startDate',
  'employmentType',
  'workFormat',
  'status',
]);
export function employeeChanges(oldValue: unknown, newValue: unknown) {
  const before =
    oldValue && typeof oldValue === 'object' ? (oldValue as Record<string, unknown>) : {};
  const after =
    newValue && typeof newValue === 'object' ? (newValue as Record<string, unknown>) : {};
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter(
      (key) => profileFields.has(key) && JSON.stringify(before[key]) !== JSON.stringify(after[key]),
    )
    .map((key) => ({ key, before: before[key], after: after[key] }));
}
export function EmployeeHistory({ employeeId }: { employeeId: string }) {
  const access = useAccess();
  const history = useRows(
    '/api/v1/employees/{employeeId}/history' as GetPath,
    {},
    { params: { employeeId } },
  );
  const departments = useCompleteRows('/api/v1/departments', access.can('departments.read'));
  const projects = useCompleteRows('/api/v1/teams', access.can('projects.read'));
  const positions = useCompleteRows('/api/v1/positions' as GetPath, access.can('positions.read'));
  const locations = useCompleteRows('/api/v1/locations', access.can('locations.read'));
  const seniorities = useCompleteRows('/api/v1/seniorities', access.can('seniorities.read'));
  const employees = useCompleteRows('/api/v1/employees', access.can('employees.read'));
  const related: Record<string, Row[]> = {
    departmentId: departments.rows,
    primaryTeamId: projects.rows,
    additionalTeamIds: projects.rows,
    positionId: positions.rows,
    locationId: locations.rows,
    seniorityId: seniorities.rows,
    lineManagerId: employees.rows,
  };
  const display = (key: string, value: unknown, snapshot?: unknown): string => {
    const labels =
      snapshot && typeof snapshot === 'object'
        ? (snapshot as { labels?: Record<string, unknown> }).labels
        : undefined;
    if (labels && Object.prototype.hasOwnProperty.call(labels, key)) {
      const saved = labels[key];
      return Array.isArray(saved)
        ? saved.map(String).join(', ') || 'Не указано'
        : String(saved || 'Не указано');
    }
    if (value === undefined || value === null || value === '') return 'Не указано';
    if (Array.isArray(value))
      return value.map((item) => display(key, item)).join(', ') || 'Не указано';
    if (related[key])
      return name(related[key]!.find((row) => row.id === value)) || 'Связанная запись';
    if (key === 'status')
      return employeeStatusLabel(String(value), true) || statusLabel(String(value));
    if (['employmentType', 'workFormat'].includes(key)) return statusLabel(String(value));
    if (key === 'startDate') return new Date(String(value)).toLocaleDateString('ru-RU');
    return String(value);
  };
  return (
    <section className="panel employee-history">
      <h2>История изменений</h2>
      {history.isLoading ? (
        <Loading />
      ) : history.error ? (
        <ErrorState error={history.error} />
      ) : !history.rows.length ? (
        <Empty title="Изменений пока нет" />
      ) : (
        <div className="employee-history-list">
          {history.rows.map((entry) => {
            const changes = employeeChanges(entry.oldValue, entry.newValue);
            return (
              <article key={entry.id} className="employee-history-entry">
                <header>
                  <strong>{String(entry.actorName || 'Пользователь')}</strong>
                  <time dateTime={String(entry.createdAt)}>
                    {new Date(String(entry.createdAt)).toLocaleString('ru-RU', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </time>
                </header>
                {entry.reason ? <p className="muted">{String(entry.reason)}</p> : null}
                {changes.length ? (
                  <dl>
                    {changes.map(({ key, before, after }) => (
                      <div key={key}>
                        <dt>{fieldLabel(key)}</dt>
                        <dd>
                          <span className="muted">{display(key, before, entry.oldValue)}</span>
                          <span aria-label="изменено на">→</span>
                          <strong>{display(key, after, entry.newValue)}</strong>
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p>Обновлены данные сотрудника</p>
                )}
              </article>
            );
          })}
        </div>
      )}
      {history.hasNextPage && (
        <Button disabled={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
          Показать ещё
        </Button>
      )}
    </section>
  );
}
